import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAppSelector } from '@/store/hooks'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Clock, Mail, User, AlertCircle, CheckCircle } from 'lucide-react'
import { useToast } from '@/hooks/useToast'
import { api } from '@/services/api'

// Validation schema for access request form
const accessRequestSchema = z.object({
  message: z.string()
    .min(10, 'Please provide a detailed message (minimum 10 characters)')
    .max(1000, 'Message too long (maximum 1000 characters)'),
  requestedRole: z.enum(['HR_ADMIN', 'GENERAL_MANAGER']).optional(),
  department: z.string().optional()
})

type AccessRequestForm = z.infer<typeof accessRequestSchema>

const PendingApprovalPage: React.FC = () => {
  const { user } = useAppSelector(state => state.auth)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasSubmittedRequest, setHasSubmittedRequest] = useState(false)
  const { toast } = useToast()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm<AccessRequestForm>({
    resolver: zodResolver(accessRequestSchema)
  })

  const requestedRole = watch('requestedRole')

  const onSubmit = async (data: AccessRequestForm) => {
    if (!user) return

    setIsSubmitting(true)
    try {
      await api.post('/pending/request-access', data)
      
      setHasSubmittedRequest(true)
      toast.success('Request Submitted', 'Your access request has been sent to administrators for review.')
    } catch (error: any) {
      toast.error('Error', error.response?.data?.message || 'Failed to submit access request')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
          <p className="text-muted-foreground">Please log in to continue.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/20">
              <Clock className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-2">Account Pending Approval</h1>
          <p className="text-muted-foreground text-lg">
            Your account is currently awaiting administrator approval to access the system features.
          </p>
        </div>

        {/* User Information Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Your Account Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Name:</span>
              <span>{user.firstName} {user.lastName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Email:</span>
              <span>{user.email}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Status:</span>
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                <Clock className="h-3 w-3 mr-1" />
                Pending Approval
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Access Request Form or Success Message */}
        {hasSubmittedRequest ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Request Submitted Successfully!</h3>
                <p className="text-muted-foreground mb-4">
                  Your access request has been sent to the administrators. You will receive an email notification 
                  once your account has been reviewed and approved.
                </p>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900 dark:text-blue-100">What happens next?</p>
                      <p className="text-blue-700 dark:text-blue-300 mt-1">
                        An administrator will review your request and assign you an appropriate role. 
                        You'll receive an email confirmation once approved, and you can then log in to access the system.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Request Access</CardTitle>
              <CardDescription>
                Help administrators understand your role and access needs by providing additional information.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="requestedRole">Requested Role (Optional)</Label>
                  <Select onValueChange={(value) => setValue('requestedRole', value as 'HR_ADMIN' | 'GENERAL_MANAGER')}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role if you know what you need" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HR_ADMIN">HR Administrator</SelectItem>
                      <SelectItem value="GENERAL_MANAGER">General Manager</SelectItem>
                    </SelectContent>
                  </Select>
                  {requestedRole && (
                    <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        {requestedRole === 'HR_ADMIN' 
                          ? 'HR Administrator: Access to HR documents, policies, and employee management features.'
                          : 'Branch Manager: Access to operations data, branch performance metrics, and PowerBI reports.'
                        }
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department">Department/Team (Optional)</Label>
                  <Input
                    id="department"
                    placeholder="e.g., Human Resources, Operations, IT"
                    {...register('department')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">
                    Message to Administrator
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Please describe your role, department, and what features you need access to. Include any relevant details that will help administrators assign you the correct permissions."
                    rows={5}
                    {...register('message')}
                    className={errors.message ? 'border-destructive' : ''}
                  />
                  {errors.message && (
                    <p className="text-sm text-destructive">{errors.message.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Provide details about your role, responsibilities, and what system features you need access to.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? 'Submitting Request...' : 'Submit Access Request'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Help Section */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">Need Help?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              If you have questions about the approval process or need immediate assistance, please contact your system administrator.
            </p>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                For urgent requests, contact: <span className="font-medium">admin@mmthrift.com</span>
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default PendingApprovalPage